import Bugsnag from '@bugsnag/js';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Icon from 'dpl/common/components/Icon';
import SmartImage from 'dpl/components/SmartImage';

export const DEFAULT_BORDER_COLOR = '#04041514';

export const AVATAR_SIZES = {
  default: { fontSize: 16, diameter: 48, padding: '11px', iconSize: '26px' },
  xs: { fontSize: 10, diameter: 24, padding: '4px', iconSize: '16px' },
  sm: { fontSize: 12, diameter: 32, padding: '6px', iconSize: '20px' },
  xxm: { fontSize: 14, diameter: 40, padding: '8px', iconSize: '24px' },
  xm: { fontSize: 16, diameter: 48, padding: '10px', iconSize: '26px' },
  md: { fontSize: 16, diameter: 56, padding: '12px', iconSize: '28px' },
  lg: { fontSize: 20, diameter: 64, padding: '16px', iconSize: '32px' },
  xl: { fontSize: 20, diameter: 72, padding: '18px', iconSize: '36px' },
  xxl: { fontSize: 20, diameter: 80, padding: '20px', iconSize: '40px' }
};

export const AVATAR_COLORS = [
  // purple
  { background: '#F5EEFD', color: '#321F43' },
  // green
  { background: '#F1F5E9', color: '#021D1C' },
  // red
  { background: '#F6E8E7', color: '#440104' },
  // yellow
  { background: '#FAF7F2', color: '#212104' },
  // blue
  { background: '#EBF7FE', color: '#283E52' }
];

const DEFAULT_SHAPE = 'round';
const SHAPES = {
  round: 'br-100',
  square: 'br2'
};

export default function UserAvatar({ user, size, shape, className }) {
  const { fontSize, diameter, padding, iconSize } =
    AVATAR_SIZES[size] || AVATAR_SIZES.default;
  const shapeStyles = SHAPES[shape || DEFAULT_SHAPE];

  const classes = classnames(
    'UserAvatar font-serif fw-medium flex items-center justify-center tc ttu ba',
    shapeStyles,
    className
  );

  const styles = {
    width: diameter,
    height: diameter,
    borderColor: DEFAULT_BORDER_COLOR
  };

  if (!user || !user.initials) {
    return (
      <div
        className={classnames('bg-white', classes)}
        style={{
          ...styles,
          ...(padding && {
            padding
          })
        }}
      >
        <Icon
          name="fetch-profile"
          className="secondary"
          width={iconSize || '24px'}
          height={iconSize || '24px'}
        />
      </div>
    );
  }

  // user id could come from several sources depending on the type of user passed in
  // (may just be breeder or buyer profile because web proptypes aren't as good as typescript)
  let userId;

  if (user.user_id) {
    userId = user.user_id;
  } else if (user.user?.id) {
    userId = user.user.id;
  } else if (user.id) {
    userId = user.id;
  } else {
    userId = 0;
    Bugsnag.notify(
      new Error('UserAvatar: unable to determine user_id from user object'),
      event => {
        event.severity = 'warning';
        event.addMetadata('user', {
          userKeys: Object.keys(user)
        });
      }
    );
  }

  const colorIndex = userId % AVATAR_COLORS.length;
  const { background, color } = AVATAR_COLORS[colorIndex];

  return (
    <div
      className={classes}
      style={{
        ...styles,
        background,
        color,
        fontSize,
        fontWeight: 500
      }}
    >
      {user.profile_photo_url ? (
        <SmartImage
          Tag="div"
          src={user.profile_photo_url}
          className={classnames('bg-center cover h-100 w-100', shapeStyles)}
        />
      ) : (
        user.initials
      )}
    </div>
  );
}

UserAvatar.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    user_id: PropTypes.number,
    initials: PropTypes.string,
    profile_photo_url: PropTypes.string,
    user: {
      id: PropTypes.number
    }
  }),
  shape: PropTypes.oneOf(Object.keys(SHAPES)),
  size: PropTypes.oneOf(Object.keys(AVATAR_SIZES)),
  className: PropTypes.string
};

UserAvatar.defaultProps = {
  size: 'default',
  className: null,
  user: null,
  shape: null
};
